import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Banknote, Lock, Crown } from "lucide-react";
import { useLang } from "@/lib/lang-context";
import { useAuth } from "@/lib/auth-context";

const COPY = {
  it: {
    eyebrow: "Mutuo & cashflow",
    quote:
      "Il prezzo al metro quadro ti dice se stai comprando bene, ma il cashflow ti dice se sopravviverai all'investimento.",
    title: "Cashflow annuale stimato",
    sub: "Confronto fra entrata netta da affitto long-term e rata annua del mutuo.",
    rateLabel: "Tasso mutuo (TAN)",
    downLabel: "Anticipo",
    yearsLabel: "Durata mutuo",
    years: "anni",
    monthlyInstallment: "Rata mensile",
    annualInstallment: "Rata annua",
    annualIncome: "Entrata netta annua",
    cashflow: "Cashflow annuo",
    positive: "Positivo — la rendita copre il mutuo",
    negative: "Negativo — esborso annuo richiesto",
    disclaimer:
      "Stima indicativa. Calcolo a rata costante (ammortamento alla francese), tassi medi di mercato italiano 2026. Non costituisce consulenza finanziaria.",
    lockedCta:
      "Vuoi inserire il tasso esatto applicato dalla tua banca o i dati precisi relativi al tuo mutuo? Passa a Premium per personalizzare il tuo Cashflow.",
    upgradeBtn: "Passa a Premium",
    chartEntry: "Entrata",
    chartMortgage: "Mutuo",
    chartCashflow: "Cashflow",
    rentalNeeded:
      "La stima del cashflow appare quando l'analisi include una rendita long-term valida (no terreni, no rudere).",
    priceNeeded:
      "Per stimare il mutuo ci serve un prezzo di acquisto. Quando l'analisi non riesce a estrarlo, questa sezione resta vuota.",
    customised: "Cashflow personalizzato",
  },
  en: {
    eyebrow: "Mortgage & cashflow",
    quote:
      "Price per square metre tells you if you're buying well; cashflow tells you if you'll survive the investment.",
    title: "Estimated annual cashflow",
    sub: "Comparison between net long-term rental income and annual mortgage payment.",
    rateLabel: "Mortgage rate (TAN)",
    downLabel: "Down payment",
    yearsLabel: "Mortgage term",
    years: "years",
    monthlyInstallment: "Monthly payment",
    annualInstallment: "Annual payment",
    annualIncome: "Net annual income",
    cashflow: "Annual cashflow",
    positive: "Positive — rent covers the mortgage",
    negative: "Negative — annual cash outlay required",
    disclaimer:
      "Indicative estimate. Constant-payment amortisation (French method), Italian 2026 market averages. Not financial advice.",
    lockedCta:
      "Want to plug in your bank's actual rate or the precise mortgage terms? Upgrade to Premium to personalise your cashflow.",
    upgradeBtn: "Go Premium",
    chartEntry: "Income",
    chartMortgage: "Mortgage",
    chartCashflow: "Cashflow",
    rentalNeeded:
      "The cashflow estimate appears when the analysis includes a valid long-term rental yield (no land, no ruin).",
    priceNeeded:
      "We need a purchase price to estimate the mortgage. When the analysis can't extract one, this section stays empty.",
    customised: "Customised cashflow",
  },
};

// Industrial defaults — Italian mortgage market averages, Feb 2026.
const DEFAULT_RATE_PCT = 3.5;
const DEFAULT_DOWN_PCT = 20;
const DEFAULT_YEARS = 25;

/**
 * French-method amortisation monthly payment.
 *   M = P × i / (1 - (1 + i)^-n)
 * where i is the monthly rate and n the number of months.
 */
function monthlyPayment(principal, annualRatePct, years) {
  if (principal <= 0) return 0;
  if (years <= 0) return principal;
  const i = annualRatePct / 100 / 12;
  const n = years * 12;
  if (i === 0) return principal / n;
  return (principal * i) / (1 - Math.pow(1 + i, -n));
}

const fmtEur = (n) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));

const MortgageCashflow = ({
  price,
  rental,
  paidReport,
  onOpenPaywall,
  annualSaving = 0,
  rate: rateProp,
  setRate: setRateProp,
  down: downProp,
  setDown: setDownProp,
  years: yearsProp,
  setYears: setYearsProp,
}) => {
  const { lang } = useLang();
  const { user } = useAuth();
  const c = COPY[lang] || COPY.it;

  // Premium = paid_report (per-analysis) OR user with premium tier OR admin.
  const isPremium =
    paidReport === true ||
    user?.tier === "premium" ||
    user?.tier === "admin";

  // Support both controlled (props from EvaluationSheet, used for the
  // Scenario Snapshots restore flow) and uncontrolled usage. When the
  // parent passes setters we delegate; otherwise we keep local state.
  const [internalRate, setInternalRate] = useState(DEFAULT_RATE_PCT);
  const [internalDown, setInternalDown] = useState(DEFAULT_DOWN_PCT);
  const [internalYears, setInternalYears] = useState(DEFAULT_YEARS);

  const rate = rateProp !== undefined ? rateProp : internalRate;
  const down = downProp !== undefined ? downProp : internalDown;
  const years = yearsProp !== undefined ? yearsProp : internalYears;
  const setRate = setRateProp || setInternalRate;
  const setDown = setDownProp || setInternalDown;
  const setYears = setYearsProp || setInternalYears;

  // Hard prerequisites: need a price AND a valid long-term rental income.
  // `annualSaving` is added on top of the net rent — currently used to
  // model the user toggling "no condo fees" upstream (CondoFees toggle).
  const baseNetIncome = rental?.longterm?.annual_net_eur ?? 0;
  const annualNetIncome = baseNetIncome + (annualSaving || 0);
  const hasPrice = Number(price) > 0;
  const hasRental = Number(baseNetIncome) > 0;

  const data = useMemo(() => {
    if (!hasPrice || !hasRental) return null;
    const downAmount = price * (down / 100);
    const principal = Math.max(0, price - downAmount);
    const m = monthlyPayment(principal, rate, years);
    const annualPayment = m * 12;
    const annualCashflow = annualNetIncome - annualPayment;
    return {
      principal,
      downAmount,
      monthlyPayment: m,
      annualPayment,
      annualCashflow,
    };
  }, [price, down, rate, years, annualNetIncome, hasPrice, hasRental]);

  if (!hasPrice) {
    return (
      <section className="px-6 md:px-10 py-6 border-t border-[#E6E4DD]" data-testid="mortgage-cashflow-empty">
        <div className="label-eyebrow flex items-center gap-2">
          <Banknote size={12} strokeWidth={1.5} />
          {c.eyebrow}
        </div>
        <p className="mt-3 text-sm text-[#6C706D]">{c.priceNeeded}</p>
      </section>
    );
  }
  if (!hasRental) {
    return (
      <section className="px-6 md:px-10 py-6 border-t border-[#E6E4DD]" data-testid="mortgage-cashflow-empty">
        <div className="label-eyebrow flex items-center gap-2">
          <Banknote size={12} strokeWidth={1.5} />
          {c.eyebrow}
        </div>
        <blockquote className="mt-4 border-l-4 border-[#4A5D23] pl-4 font-serif-display text-xl text-[#282C2A] italic leading-snug">
          "{c.quote}"
        </blockquote>
        <p className="mt-3 text-sm text-[#6C706D]">{c.rentalNeeded}</p>
      </section>
    );
  }

  const chartData = [
    { label: c.chartEntry, value: annualNetIncome, color: "#4A5D23" },
    { label: c.chartMortgage, value: -data.annualPayment, color: "#8B2E1F" },
    {
      label: c.chartCashflow,
      value: data.annualCashflow,
      color: data.annualCashflow >= 0 ? "#4A5D23" : "#8B2E1F",
    },
  ];

  return (
    <section
      data-testid="mortgage-cashflow"
      className="px-6 md:px-10 py-8 border-t border-[#E6E4DD]"
    >
      <div className="label-eyebrow flex items-center gap-2">
        <Banknote size={12} strokeWidth={1.5} />
        {c.eyebrow}
      </div>

      <blockquote className="mt-4 border-l-4 border-[#4A5D23] pl-4 font-serif-display text-xl md:text-2xl text-[#282C2A] italic leading-snug max-w-3xl">
        "{c.quote}"
      </blockquote>

      <h3 className="font-serif-display text-2xl md:text-3xl text-[#282C2A] mt-6">
        {c.title}
      </h3>
      <p className="text-sm text-[#6C706D] mt-1">{c.sub}</p>

      {/* Headline numbers */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-[#E6E4DD] p-4 bg-[#FBFAF7]">
          <div className="text-[10px] uppercase tracking-widest text-[#6C706D]">
            {c.monthlyInstallment}
          </div>
          <div
            className="font-mono text-xl mt-1 text-[#8B2E1F]"
            data-testid="mortgage-monthly"
          >
            {fmtEur(data.monthlyPayment)}
          </div>
          <div className="text-[11px] text-[#6C706D] mt-1">
            {c.annualInstallment}: <span className="font-mono">{fmtEur(data.annualPayment)}</span>
          </div>
        </div>
        <div className="border border-[#E6E4DD] p-4 bg-[#FBFAF7]">
          <div className="text-[10px] uppercase tracking-widest text-[#6C706D]">
            {c.annualIncome}
          </div>
          <div className="font-mono text-xl mt-1 text-[#4A5D23]">
            {fmtEur(annualNetIncome)}
          </div>
          <div className="text-[11px] text-[#6C706D] mt-1">
            {rental?.longterm?.net_yield_pct
              ? `${rental.longterm.net_yield_pct}% net yield`
              : ""}
          </div>
        </div>
        <div
          className={`border-2 p-4 ${
            data.annualCashflow >= 0
              ? "border-[#4A5D23] bg-[#4A5D23]/5"
              : "border-[#8B2E1F] bg-[#8B2E1F]/5"
          }`}
        >
          <div className="text-[10px] uppercase tracking-widest text-[#6C706D]">
            {c.cashflow}
          </div>
          <div
            className={`font-mono text-2xl mt-1 ${
              data.annualCashflow >= 0 ? "text-[#4A5D23]" : "text-[#8B2E1F]"
            }`}
            data-testid="mortgage-cashflow-value"
          >
            {data.annualCashflow >= 0 ? "+" : ""}
            {fmtEur(data.annualCashflow)}
          </div>
          <div className="text-[11px] mt-1 text-[#6C706D]">
            {data.annualCashflow >= 0 ? c.positive : c.negative}
          </div>
        </div>
      </div>

      {/* Bar chart */}
      <div className="mt-8 h-64" data-testid="mortgage-cashflow-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
            <XAxis
              dataKey="label"
              stroke="#6C706D"
              tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#282C2A" }}
            />
            <YAxis
              stroke="#6C706D"
              tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#6C706D" }}
              tickFormatter={(v) => `${Math.round(v / 1000)}k`}
            />
            <Tooltip
              cursor={{ fill: "#F0EFEB" }}
              formatter={(v) => fmtEur(v)}
              contentStyle={{
                borderRadius: 0,
                border: "2px solid #282C2A",
                fontFamily: "JetBrains Mono",
                fontSize: 12,
              }}
            />
            <ReferenceLine y={0} stroke="#282C2A" strokeWidth={1.5} />
            <Bar dataKey="value" radius={[0, 0, 0, 0]}>
              {chartData.map((entry, idx) => (
                <Cell key={idx} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Controls — editable for premium, locked for free */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 relative">
        {!isPremium && (
          <div
            className="absolute inset-0 z-10 bg-[#FBFAF7]/70 backdrop-blur-[1px] cursor-not-allowed"
            aria-hidden="true"
          />
        )}
        <ControlSlider
          label={c.rateLabel}
          suffix="%"
          value={rate}
          min={1}
          max={8}
          step={0.1}
          locked={!isPremium}
          onChange={setRate}
          dataTestid="mortgage-rate-slider"
        />
        <ControlSlider
          label={c.downLabel}
          suffix="%"
          value={down}
          min={10}
          max={80}
          step={5}
          locked={!isPremium}
          onChange={setDown}
          dataTestid="mortgage-down-slider"
        />
        <ControlSlider
          label={c.yearsLabel}
          suffix={` ${c.years}`}
          value={years}
          min={10}
          max={30}
          step={1}
          locked={!isPremium}
          onChange={setYears}
          dataTestid="mortgage-years-slider"
        />
      </div>

      {!isPremium && (
        <div className="mt-6 border-2 border-[#282C2A] bg-[#282C2A] text-white p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
          <Lock size={32} strokeWidth={1.5} className="text-[#C9B96A] shrink-0" />
          <div className="flex-1">
            <div className="label-eyebrow text-[#C9B96A] mb-2">
              {c.customised}
            </div>
            <p className="text-sm leading-relaxed">{c.lockedCta}</p>
          </div>
          <button
            type="button"
            data-testid="mortgage-upgrade-btn"
            onClick={onOpenPaywall}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C9B96A] text-[#282C2A] font-semibold tracking-wider uppercase text-xs hover:bg-[#D9C97A] transition-colors shrink-0"
          >
            <Crown size={14} strokeWidth={1.75} />
            {c.upgradeBtn}
          </button>
        </div>
      )}

      <p className="mt-4 text-[11px] text-[#6C706D] italic">{c.disclaimer}</p>
    </section>
  );
};

const ControlSlider = ({
  label,
  suffix,
  value,
  min,
  max,
  step,
  locked,
  onChange,
  dataTestid,
}) => (
  <div className={locked ? "opacity-60 select-none" : ""}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] uppercase tracking-widest text-[#6C706D] flex items-center gap-1.5">
        {locked && <Lock size={10} strokeWidth={1.75} />}
        {label}
      </span>
      <span className="font-mono text-sm text-[#282C2A]">
        {value}
        {suffix}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      disabled={locked}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      data-testid={dataTestid}
      className={`w-full accent-[#4A5D23] ${
        locked ? "cursor-not-allowed" : "cursor-pointer"
      }`}
    />
  </div>
);

export default MortgageCashflow;
